import {exhaustive} from '@freckle/exhaustive'
import type {MathQuestionAttrs} from '@freckle/educator-entities/ts/math/common/models/questions'
import type {MathQuestionBucketMetadataAttrs} from '@freckle/educator-entities/ts/math/common/models/questions/question-bucket'
import type {MathQuestionFillInTheBlanksMetadataAttrs} from '@freckle/educator-entities/ts/math/common/models/questions/question-fill-in-the-blanks'
import type {MathQuestionMultipleAnswersMetadataAttrs} from '@freckle/educator-entities/ts/math/common/models/questions/question-multiple-answers'
import type {MathQuestionMultipleChoiceMetadataAttrs} from '@freckle/educator-entities/ts/math/common/models/questions/question-multiple-choice'
import type {MathQuestionRegularMetadataAttrs} from '@freckle/educator-entities/ts/math/common/models/questions/question-regular'
import {EnableMathJax} from '@freckle/educator-materials/ts/components/enable-math-jax'
import type {BucketCorrectAnswertFormatT} from '@freckle/educator-materials/ts/math/common/components/correct-answers/bucket-answer'
import {BucketAnswer} from '@freckle/educator-materials/ts/math/common/components/correct-answers/bucket-answer'
import {MathQuestionFillInTheBlanks} from '@freckle/educator-materials/ts/math/common/components/correct-answers/fill-in-the-blanks'
import {FillInTheBlanksReportCorrectAnswers} from '@freckle/educator-materials/ts/math/common/components/correct-answers/fill-in-the-blanks-report-answers'
import type {AlternationT} from '@freckle/educator-materials/ts/math/common/components/correct-answers/formatted-answer'
import {FormattedAnswer} from '@freckle/educator-materials/ts/math/common/components/correct-answers/formatted-answer'
import type {
  GraphQuestionMetadataAttrs,
  GraphCorrectAnswerFormatT,
} from '@freckle/educator-materials/ts/math/common/components/correct-answers/graph-answer'
import {GraphAnswer} from '@freckle/educator-materials/ts/math/common/components/correct-answers/graph-answer'

export type AnswerFormatT =
  | {
      tag: 'formatted-answer'
      metadata:
        | {
            answerType: 'singular_or'
            content: MathQuestionRegularMetadataAttrs
          }
        | {
            answerType: 'multiple_choice'
            content: MathQuestionMultipleChoiceMetadataAttrs
          }
        | {
            answerType: 'multiple_answers'
            content: MathQuestionMultipleAnswersMetadataAttrs
          }
    }
  | {
      tag: 'bucket-answer'
      format: BucketCorrectAnswertFormatT
      metadata: MathQuestionBucketMetadataAttrs
    }
  | {
      tag: 'graph-answer'
      format: GraphCorrectAnswerFormatT
      metadata: GraphQuestionMetadataAttrs
    }
  | {
      tag: 'fill-in-the-blanks'
      format: 'report' | 'non-interactive-ui'
      metadata: MathQuestionFillInTheBlanksMetadataAttrs
    }

const CorrectAnswersForQuestion = (props: Props) => {
  const {question, format} = props

  switch (format.tag) {
    case 'formatted-answer':
      return (
        <FormattedAnswer
          metadata={format.metadata.content}
          alternationToken={getAlternationToken(format.metadata.answerType)}
        />
      )
    case 'bucket-answer':
      return <BucketAnswer metadata={format.metadata} format={format.format} />
    case 'graph-answer':
      return <GraphAnswer metadata={format.metadata} format={format.format} />
    case 'fill-in-the-blanks':
      switch (format.format) {
        case 'report':
          return <FillInTheBlanksReportCorrectAnswers metadata={format.metadata} />
        case 'non-interactive-ui':
          return (
            <MathQuestionFillInTheBlanks
              question={question}
              metadata={format.metadata}
              revealCorrectAnswers
            />
          )
        default:
          return exhaustive((format as {format: never}).format)
      }
    default:
      return exhaustive(format)
  }
}

type Props = {
  question: MathQuestionAttrs
  format: AnswerFormatT
}

export const CorrectAnswers = (props: Props) => {
  const {format, question} = props

  return (
    <EnableMathJax>
      <CorrectAnswersForQuestion question={question} format={format} />
    </EnableMathJax>
  )
}

export const CorrectAnswersForReport = ({question}: {question: MathQuestionAttrs}) => {
  const {metadata} = question

  switch (metadata.answerType) {
    case 'bucket':
      return (
        <CorrectAnswers
          question={question}
          format={{tag: 'bucket-answer', format: 'text', metadata: metadata.content}}
        />
      )
    case 'singular_or':
      return <CorrectAnswers question={question} format={{tag: 'formatted-answer', metadata}} />
    case 'multiple_choice':
      return <CorrectAnswers question={question} format={{tag: 'formatted-answer', metadata}} />
    case 'multiple_answers':
      return <CorrectAnswers question={question} format={{tag: 'formatted-answer', metadata}} />
    case 'fill_in_the_blanks':
      return (
        <CorrectAnswers
          question={question}
          format={{
            tag: 'fill-in-the-blanks',
            metadata: metadata.content,
            format: 'report',
          }}
        />
      )
    case 'graph_exponential':
      return (
        <CorrectAnswers
          question={question}
          format={{tag: 'graph-answer', metadata, format: 'text'}}
        />
      )
    case 'graph_linear_inequality':
      return (
        <CorrectAnswers
          question={question}
          format={{tag: 'graph-answer', metadata, format: 'text'}}
        />
      )
    case 'graph_linear':
      return (
        <CorrectAnswers
          question={question}
          format={{tag: 'graph-answer', metadata, format: 'text'}}
        />
      )
    case 'graph_quadratic':
      return (
        <CorrectAnswers
          question={question}
          format={{tag: 'graph-answer', metadata, format: 'text'}}
        />
      )
    case 'graph_scatter_points':
      return (
        <CorrectAnswers
          question={question}
          format={{tag: 'graph-answer', metadata, format: 'text'}}
        />
      )
    default:
      return exhaustive(metadata)
  }
}

type AnswerT = 'singular_or' | 'multiple_choice' | 'multiple_answers'
const getAlternationToken = (answerType: AnswerT): AlternationT => {
  switch (answerType) {
    case 'singular_or':
      return 'or'
    case 'multiple_choice':
      return 'or'
    case 'multiple_answers':
      return 'and'
    default:
      return exhaustive(answerType)
  }
}
